// libs
import React, { memo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';

// component
import OtpFlow from 'shared/layouts/OTPVerification/Mobile';
import Logo from 'shared/components/Logo';
import RouteChunkRenderer from 'shared/components/RouteChunkRenderer/RouteChunkRenderer';

// Helpers
import { actionsForMagento } from './helpers';
import { Header, InnerContainer, OuterContainer } from './styles';
import { getStoreId } from 'shared/store/app/selectors';
import { DEFAULT_STORE, getStoreUrl } from 'shared/helpers/storeUrl';

//constants
import { ROUTES, urls } from 'shared/api/urls';
import { LOGO_TYPE } from 'shared/constants';

const containDomainName = (text: string) => /(https:\/\/|http:\/\/|www\.)\S+/i.test(text);

interface IMobileAuthFlowProps {
  isEnabledForMobile: boolean;
  isEnabledForDesktop: boolean;
  isUserLoggedIn: boolean;
  isIndianUser: boolean;
  isUserDataFetching: boolean;
}

function MobileAuthFlow({ isUserLoggedIn, isUserDataFetching }: IMobileAuthFlowProps) {
  const { search } = useLocation();
  const history = useHistory();
  const storeId = useSelector(getStoreId);

  const URLParams = new URLSearchParams(search);
  const rawRedirectionURL = (URLParams.get('redirectURL')  ||getStoreUrl('/', storeId)).replace('@', '?');
  const redirectionURL = getStoreUrl(rawRedirectionURL, storeId);
  const onCancel = URLParams.get('onCancel');
  const router = URLParams.get('route');
  const isLegacyCheckoutFlow = router === 'checkout';
  const isLegacyDiscoveryFlow = router === 'discovery';
  const comingViaLegacyCode = isLegacyCheckoutFlow || isLegacyDiscoveryFlow;

  // Determine OTP flow type based on redirectionURL
  const otpFlowType = 'authorization';

  const onHide = (isCancelled = false) => {
    const statusQS = new URLSearchParams();
    const actionStatus = isCancelled ? 'cancelled' : 'success';
    statusQS.append('actionStatus', actionStatus);
    const URL = `${redirectionURL}${redirectionURL.indexOf('?') < 0 ? '?' : '&'
      }${statusQS.toString()}`;

    if (comingViaLegacyCode) {
      actionsForMagento(isCancelled, URL);
      return;
    }

    if (isCancelled && onCancel === 'back') {
      window.history.back();
      return;
    }

    if (URL.includes(window.location.host)) {
      window.location.replace(URL);
      return;
    }

    if (containDomainName(URL)) {
      history.replace(getStoreUrl('/', storeId));
      return;
    }

    if (storeId !== DEFAULT_STORE) {
      window.location.replace(URL);
      return;
    }

    history.replace(URL);
  };

  return (
    <RouteChunkRenderer
      mobileComponent={
        <OtpFlow
          hide={onHide}
          isUserLoggedIn={isUserLoggedIn}
          isCheckoutFlow={true}
          isUserDataFetching={isUserDataFetching}
          type={otpFlowType}
        />
      }
      desktopComponent={
        <OuterContainer>
          <Header>
            <a href={getStoreUrl(urls[ROUTES.HOME], storeId)} aria-label="Logo">
              <Logo type={LOGO_TYPE.DWEB} />
            </a>
          </Header>
          <InnerContainer>
            <OtpFlow
              hide={onHide}
              isUserLoggedIn={isUserLoggedIn}
              isCheckoutFlow={isLegacyCheckoutFlow}
              isUserDataFetching={isUserDataFetching}
              type={otpFlowType}
            />
          </InnerContainer>
        </OuterContainer>
      }
    />
  );
}

export default memo(MobileAuthFlow);
